<div class="welcome-page">
  @if (isLoading) {
    <!-- Show nothing while determining redirect -->
  } @else {
  <!-- Header -->
  <div class="welcome-header">
    <div class="header-content">
      <div>
        <h1 class="welcome-title">
          Welcome to FFL XCEL ,
          @if (sharedService.company_info?.company_name) {
            <span class="company-name"> {{ sharedService.company_info.company_name }}</span>
          }
        </h1>
        <p class="welcome-subtitle">
          Complete the steps below to get your account ready. Required items must be set up before you can
          take full advantage of the software.
        </p>
      </div>
      <button class="btn btn-success btn-go-dashboard" (click)="goToDashboard()">
        <i class="fas fa-tachometer-alt me-2"></i>Go to Dashboard
      </button>
    </div>

    <!-- Progress Bar -->
    <div class="progress-section">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <span class="progress-label">Setup Progress</span>
        <span class="progress-label">{{ completedCount }} / {{ tasks.length }} complete</span>
      </div>
      <div class="progress" style="height: 10px; border-radius: 8px;">
        <div
          class="progress-bar"
          role="progressbar"
          [style.width.%]="progressPercent"
          [ngClass]="{
            'bg-success': allRequiredComplete,
            'bg-warning': !allRequiredComplete
          }"
          style="transition: width 0.5s ease; border-radius: 8px;"
        ></div>
      </div>
    </div>
  </div>

  <!-- Required Tasks -->
  <button class="section-toggle required-label" type="button" (click)="toggleRequired()">
    <span><i class="fas fa-star me-2"></i>Required Setup</span>
    <span class="section-meta">
      {{ requiredTasks.length }} items
      <i class="fas ms-2" [ngClass]="requiredCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
    </span>
  </button>
  <div [collapse]="requiredCollapsed">
  <div class="tasks-grid">
    @for (task of requiredTasks; track task.id) {
      <div class="task-card" [ngClass]="{ 'task-complete': task.isComplete }">
        <div class="task-main-row">
          <button type="button" class="task-summary-btn" (click)="toggleTask(task.id)">
            <div class="task-icon-wrap" [ngClass]="{ 'icon-complete': task.isComplete }">
              <i [class]="task.icon"></i>
            </div>
            <div class="task-body">
              <div class="task-header">
                <h5 class="task-title">{{ task.title }}</h5>
                @if (task.isComplete) {
                  <span class="badge bg-success status-badge">
                    <i class="fas fa-check me-1"></i>Complete
                  </span>
                } @else {
                  <span class="badge bg-warning text-dark status-badge">
                    <i class="fas fa-clock me-1"></i>Pending
                  </span>
                }
              </div>
            </div>
          </button>
          <div class="task-actions">
            <button
              class="btn btn-setup"
              [ngClass]="task.isComplete ? 'btn-outline-success' : 'btn-primary'"
              (click)="goToTask(task)"
            >
              <i class="fas me-2" [ngClass]="task.isComplete ? 'fa-pencil' : 'fa-arrow-right'"></i>
              {{ task.isComplete ? 'Review' : 'Set Up' }}
            </button>
            <button type="button" class="btn btn-outline-light btn-sm ms-2" (click)="toggleTask(task.id)">
              <i class="fas" [ngClass]="isTaskCollapsed(task.id) ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
            </button>
          </div>
        </div>
        <div [collapse]="isTaskCollapsed(task.id)" class="task-details-wrap">
          <p class="task-description">{{ task.description }}</p>
          <div class="task-details-layout">
            <div class="task-details-main">
              <div class="task-functions-title">Functions tied to this item</div>
              <ul class="task-function-list">
                @for (detail of task.functionDetails; track detail) {
                  <li>{{ detail }}</li>
                }
              </ul>
            </div>
            <aside class="task-disabled-panel">
              <div class="task-disabled-title">Disabled until setup</div>
              <ul class="task-disabled-list">
                @for (item of task.disabled_functionality; track item) {
                  <li>{{ item }}</li>
                }
              </ul>
            </aside>
          </div>
        </div>
      </div>
    }
  </div>
  </div>

  <!-- Recommended Tasks -->
  <button class="section-toggle recommended-label" type="button" (click)="toggleRecommended()">
    <span><i class="fas fa-thumbs-up me-2"></i>Recommended Setup</span>
    <span class="section-meta">
      {{ recommendedTasks.length }} items
      <i class="fas ms-2" [ngClass]="recommendedCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
    </span>
  </button>
  <div [collapse]="recommendedCollapsed">
  <div class="tasks-grid">
    @for (task of recommendedTasks; track task.id) {
      <div class="task-card" [ngClass]="{ 'task-complete': task.isComplete }">
        <div class="task-main-row">
          <button type="button" class="task-summary-btn" (click)="toggleTask(task.id)">
            <div class="task-icon-wrap" [ngClass]="{ 'icon-complete': task.isComplete, 'icon-recommended': !task.isComplete }">
              <i [class]="task.icon"></i>
            </div>
            <div class="task-body">
              <div class="task-header">
                <h5 class="task-title">{{ task.title }}</h5>
                @if (task.isComplete) {
                  <span class="badge bg-success status-badge">
                    <i class="fas fa-check me-1"></i>Complete
                  </span>
                } @else {
                  <span class="badge bg-secondary status-badge">
                    <i class="fas fa-circle-dot me-1"></i>Optional
                  </span>
                }
              </div>
            </div>
          </button>
          <div class="task-actions">
            <button
              class="btn btn-setup"
              [ngClass]="task.isComplete ? 'btn-outline-success' : 'btn-outline-primary'"
              (click)="goToTask(task)"
            >
              <i class="fas me-2" [ngClass]="task.isComplete ? 'fa-pencil' : 'fa-arrow-right'"></i>
              {{ task.isComplete ? 'Review' : 'Set Up' }}
            </button>
            <button type="button" class="btn btn-outline-light btn-sm ms-2" (click)="toggleTask(task.id)">
              <i class="fas" [ngClass]="isTaskCollapsed(task.id) ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
            </button>
          </div>
        </div>
        <div [collapse]="isTaskCollapsed(task.id)" class="task-details-wrap">
          <p class="task-description">{{ task.description }}</p>
          <div class="task-details-layout">
            <div class="task-details-main">
              <div class="task-functions-title">Functions tied to this item</div>
              <ul class="task-function-list">
                @for (detail of task.functionDetails; track detail) {
                  <li>{{ detail }}</li>
                }
              </ul>
            </div>
            <aside class="task-disabled-panel">
              <div class="task-disabled-title">Disabled until setup</div>
              <ul class="task-disabled-list">
                @for (item of task.disabled_functionality; track item) {
                  <li>{{ item }}</li>
                }
              </ul>
            </aside>
          </div>
        </div>
      </div>
    }
  </div>
  </div>

  <!-- Footer CTA -->
  @if (allRequiredComplete) {
    <div class="footer-cta">
      <i class="fas fa-party-horn me-2" style="color: #f59e0b;"></i>
      All required tasks are complete — you're ready to go!
      <button class="btn btn-success ms-3" (click)="goToDashboard()">
        <i class="fas fa-tachometer-alt me-2"></i>Go to Dashboard
      </button>
    </div>
  } @else {
    <div class="footer-cta" style="opacity: 0.6;">
      <i class="fas fa-info-circle me-2"></i>
      Complete the required tasks above to unlock the full FFL XCEL experience.
      <button class="btn btn-outline-light ms-3" (click)="goToDashboard()">
        Skip for now
      </button>
    </div>
  }
  }
</div>
